import type { BroadcastChannel } from '../types/broadcast';
import type { BroadcastSend, BroadcastSendStatus } from '../types/broadcastSend';

// Stage 6A v3 §1.4 — engagement filter for the broadcast recipient table.
// Multi-select with AND logic and STRICT, delivery-aware definitions: the
// "Not X" filters deliberately exclude rows that never had the chance to engage
// (failed / bounced sends), so "opened but didn't click" surfaces actionable
// contacts rather than dead sends.

export type EngagementFilter =
  | 'opened'
  | 'not_opened'
  | 'clicked'
  | 'not_clicked'
  | 'unsubscribed'
  | 'bounced'
  | 'failed';

export const ENGAGEMENT_FILTER_OPTIONS: { value: EngagementFilter; label: string }[] = [
  { value: 'opened', label: 'Opened' },
  { value: 'not_opened', label: 'Not Opened' },
  { value: 'clicked', label: 'Clicked' },
  { value: 'not_clicked', label: 'Not Clicked' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'failed', label: 'Failed' },
];

// v4 §Change-2 — the open/click-tracking filters that only make sense for email
// (SMS has no open/click tracking). The delivery-outcome filters (unsubscribed /
// bounced / failed) apply to BOTH channels. Drives the dropdown's per-option
// disable under Channel=SMS and the recipient-table SMS-exclusion rule.
const EMAIL_ONLY_ENGAGEMENT_FILTERS: ReadonlySet<EngagementFilter> = new Set([
  'opened',
  'not_opened',
  'clicked',
  'not_clicked',
]);

export function isEmailOnlyEngagementFilter(filter: EngagementFilter): boolean {
  return EMAIL_ONLY_ENGAGEMENT_FILTERS.has(filter);
}

// Only the engagement-bearing subset of a send is needed — keeps the predicate
// trivially testable without a full BroadcastSend fixture.
export type EngagementFields = Pick<
  BroadcastSend,
  'status' | 'openedAt' | 'firstClickAt' | 'unsubscribedAt'
>;

// Whether a send satisfies ONE engagement condition (§1.4 table).
export function matchesEngagementFilter(send: EngagementFields, filter: EngagementFilter): boolean {
  switch (filter) {
    case 'opened':
      return send.openedAt != null;
    // Delivered but never opened — failed/bounced excluded (no chance to open).
    case 'not_opened':
      return send.status === 'delivered' && send.openedAt == null;
    case 'clicked':
      return send.firstClickAt != null;
    // Opened but didn't click — must have opened first (can't click unopened).
    case 'not_clicked':
      return send.status === 'delivered' && send.openedAt != null && send.firstClickAt == null;
    case 'unsubscribed':
      return send.unsubscribedAt != null;
    // v4 — delivery failures, both channels.
    case 'bounced':
      return send.status === 'bounced';
    case 'failed':
      return send.status === 'failed';
  }
}

// AND across the active set: a send matches iff it satisfies EVERY selected
// filter. An empty set matches everything (no engagement filter applied).
export function matchesAllEngagementFilters(
  send: EngagementFields,
  active: EngagementFilter[],
): boolean {
  return active.every((f) => matchesEngagementFilter(send, f));
}

// ---------- clickable stat counters (v4 §Change-3) ----------

// The metrics shown in each per-channel stat row. Clicking one drives the
// recipient-table filters; Sent maps to channel-only, Delivered to a status
// filter, the rest to an engagement filter.
export type CounterKey =
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'clicked'
  | 'bounced'
  | 'failed'
  | 'unsubscribed';

// The combined recipient-table filter state the three dropdowns share. A
// counter click sets all three at once so the highlight and the table stay in
// lockstep.
export type RecipientFilterState = {
  channel: 'all' | BroadcastChannel;
  status: 'all' | BroadcastSendStatus;
  engagement: EngagementFilter[];
};

export const DEFAULT_RECIPIENT_FILTER: RecipientFilterState = {
  channel: 'all',
  status: 'all',
  engagement: [],
};

// The filter state that clicking `key` in `channel`'s stat row applies. Sent =
// channel only; Delivered = a status filter (not engagement); the rest = a
// single engagement filter. Channel is always set to the row's channel so the
// table narrows to that channel (per §Change-3).
export function counterTargetFilter(
  key: CounterKey,
  channel: BroadcastChannel,
): RecipientFilterState {
  switch (key) {
    case 'sent':
      return { channel, status: 'all', engagement: [] };
    case 'delivered':
      return { channel, status: 'delivered', engagement: [] };
    case 'opened':
      return { channel, status: 'all', engagement: ['opened'] };
    case 'clicked':
      return { channel, status: 'all', engagement: ['clicked'] };
    case 'bounced':
      return { channel, status: 'all', engagement: ['bounced'] };
    case 'failed':
      return { channel, status: 'all', engagement: ['failed'] };
    case 'unsubscribed':
      return { channel, status: 'all', engagement: ['unsubscribed'] };
  }
}

function sameEngagementSet(a: EngagementFilter[], b: EngagementFilter[]): boolean {
  if (a.length !== b.length) return false;
  const sa = new Set(a);
  return b.every((x) => sa.has(x));
}

export function recipientFiltersEqual(a: RecipientFilterState, b: RecipientFilterState): boolean {
  return a.channel === b.channel && a.status === b.status && sameEngagementSet(a.engagement, b.engagement);
}

// A counter is "active" (highlighted) when the current filter state is exactly
// what clicking it would produce — so the highlight tracks the dropdowns and
// toggling works cleanly.
export function isCounterActive(
  key: CounterKey,
  channel: BroadcastChannel,
  state: RecipientFilterState,
): boolean {
  return recipientFiltersEqual(state, counterTargetFilter(key, channel));
}

// Clicking a counter REPLACES the filter with that counter's target (never
// unions); clicking the already-active counter toggles back to the default
// (cleared) view.
export function applyCounterClick(
  key: CounterKey,
  channel: BroadcastChannel,
  state: RecipientFilterState,
): RecipientFilterState {
  return isCounterActive(key, channel, state)
    ? DEFAULT_RECIPIENT_FILTER
    : counterTargetFilter(key, channel);
}
